import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { useTrialAuth } from '@/hooks/use-trial-auth'
import { recordError } from '@/lib/recent-errors'

// Absolute API origin. The SPA and the API live on different hosts in
// production (app.* vs api.*), so a relative '/api/v1/...' fetch hits the
// SPA's nginx and gets HTML back; every call must go through this base.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

/**
 * Resolve a photo/upload path to a full URL on the API server.
 * Paths like `/uploads/photos/...` are served from the backend, not the frontend domain.
 */
export function resolveUploadUrl(path?: string | null): string | undefined {
  if (!path) return undefined
  if (path.startsWith('http')) return path
  // Strip /api/v1 suffix to get the API origin
  const apiOrigin = API_BASE_URL.replace(/\/api\/v1\/?$/, '')
  return `${apiOrigin}${path.startsWith('/') ? '' : '/'}${path}`
}

// Token provider function that can be set from outside
let getAuthToken: (() => string | null) | null = null

export const setTokenProvider = (provider: () => string | null) => {
  getAuthToken = provider
}

// Track whether a token refresh is in progress to avoid concurrent refreshes
let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = getAuthToken?.()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        return config
      },
      (error) => {
        console.error('[API] Request interceptor error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor with automatic token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
        recordError(error.response?.status, originalRequest?.url, error.response?.data, error.message)

        if (error.response?.status === 401 && !originalRequest._retry) {
          const refreshToken = localStorage.getItem('refresh_token')

          if (refreshToken) {
            if (isRefreshing) {
              // Another refresh is in progress — wait for it
              return new Promise((resolve) => {
                addRefreshSubscriber((newToken: string) => {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`
                  resolve(this.client.request(originalRequest))
                })
              })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {
              const response = await axios.post(
                `${API_BASE_URL}/auth/refresh`,
                {},
                { headers: { Authorization: `Bearer ${refreshToken}` } }
              )

              const newAccessToken = response.data.access_token
              const newRefreshToken = response.data.refresh_token

              // Update localStorage
              localStorage.setItem('access_token', newAccessToken)
              if (newRefreshToken) {
                localStorage.setItem('refresh_token', newRefreshToken)
              }

              // Update Zustand trial auth store
              const trialState = useTrialAuth.getState()
              if (trialState.trialUser) {
                trialState.setTrialUser({
                  ...trialState.trialUser,
                  access_token: newAccessToken,
                })
              }

              isRefreshing = false
              onTokenRefreshed(newAccessToken)

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
              return this.client.request(originalRequest)
            } catch (refreshError) {
              isRefreshing = false
              refreshSubscribers = []

              // Refresh failed — clear auth and redirect to login
              localStorage.removeItem('access_token')
              localStorage.removeItem('refresh_token')
              useTrialAuth.getState().clearTrialUser()

              if (window.location.pathname.startsWith('/app')) {
                window.location.href = '/login'
              }
              return Promise.reject(refreshError)
            }
          }

          // No refresh token available — redirect to login
          if (window.location.pathname.startsWith('/app')) {
            window.location.href = '/login'
          }
        }

        // Handle 402 Payment Required — usage limit exceeded
        if (error.response?.status === 402) {
          const feature = error.response?.headers?.['x-feature'] || 'premium'
          window.dispatchEvent(
            new CustomEvent('subscription:limit-reached', {
              detail: { feature, message: error.response?.data?.detail },
            })
          )
        }

        return Promise.reject(error)
      }
    )
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config)
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.post<T>(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.put<T>(url, data, config)
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.patch<T>(url, data, config)
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T>(url, config)
  }
}

export const apiClient = new ApiClient()
export const api = apiClient // For backward compatibility

// Export the ApiClient class for services that need to extend it
export { ApiClient }

// Specialized API services
export const studentApi = {
  getProfile: (id: string) => apiClient.get(`/students/${id}`),
  updateProfile: (id: string, data: any) => apiClient.patch(`/students/${id}`, data),
  getStats: (id: string) => apiClient.get(`/students/${id}/stats`),
  getPathways: (id: string) => apiClient.get(`/students/${id}/pathways`),
  getLessons: (id: string, params?: any) => apiClient.get(`/students/${id}/lessons`, { params }),
}

export const lessonApi = {
  getAll: (params?: any) => apiClient.get('/lessons', { params }),
  getById: (id: string) => apiClient.get(`/lessons/${id}`),
  startLesson: (id: string) => apiClient.post(`/lessons/${id}/start`),
  completeLesson: (id: string, data: any) => apiClient.post(`/lessons/${id}/complete`, data),
  getProgress: (id: string) => apiClient.get(`/lessons/${id}/progress`),
}

export const assessmentApi = {
  getAll: (params?: any) => apiClient.get('/assessments', { params }),
  getById: (id: string) => apiClient.get(`/assessments/${id}`),
  submitAnswer: (id: string, questionId: string, answer: any) =>
    apiClient.post(`/assessments/${id}/questions/${questionId}/answer`, answer),
  complete: (id: string) => apiClient.post(`/assessments/${id}/complete`),
}

export const aiTutorApi = {
  chat: (message: string, context?: any) => apiClient.post('/ai/tutor/chat', { message, context }),
  getHint: (lessonId: string, questionId: string) =>
    apiClient.post('/ai/tutor/hint', { lessonId, questionId }),
  explainConcept: (concept: string, context?: any) =>
    apiClient.post('/ai/tutor/explain', { concept, context }),
}
